import React from 'react';
import PropTypes from 'prop-types';
import { Waypoint } from 'react-waypoint';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import cx from 'classnames';
import config from './config';
import TOC from './component';
import Box from '@material-ui/core/Box';
import withStores from '@studionand/ada-next/src/stores/withStores';

const jssStyles = theme => ({
  container: ({ hasAnimatedTransform, isSticky, isHeaderVisisble }) => ({
    transitionDuration: hasAnimatedTransform ? '200ms' : '200ms',
    transform: `translate(0, ${
      isSticky && !isHeaderVisisble ? theme.spacing(10) : 0
    }px)`,
    marginTop: isSticky ? 0 : 50,
    position: isSticky ? 'fixed' : null,
    top: isSticky ? 0 : null,
    left: isSticky ? 0 : null,
    zIndex: 1,
    transitionProperty: 'transform',
    transitionTimingFunction: 'ease-in-out',
    width: '100vw',
    marginLeft: 'calc(50% - 50vw)',
    marginRight: 'calc(50% - 50vw)',
  }),
  root: {
    position: 'relative',
  },
  rootSpacing: {
    marginBottom: theme.spacing(20),
  },
  waypointMarker: {
    display: 'block',
    content: ' ',
    minHeight: 0,
    minWidth: '100%',
  },
});

const useStyles = makeStyles(jssStyles);

const TocContainer = props => {
  const { isSticky, hasAnimatedTransform, isHeaderVisisble, ...rest } = props;
  const classes = useStyles({ ...props });
  return <Box className={classes.container} {...rest} />;
};

const createTitleWithToc = () => {
  @withStyles(jssStyles)
  @withStores('appStore')
  @observer
  class TitleWithToc extends React.Component { //eslint-disable-line
    static defaultProps = {
      children: null,
      isLoggedIn: false,
      dashboardAccess: false,
    };
    static propTypes = {
      page: PropTypes.object.isRequired,
      children: PropTypes.any,
      isLoggedIn: PropTypes.bool,
      dashboardAccess: PropTypes.bool,
    };

    @action.bound
    setSticky(shouldStick) {
      if (this.isSticky) this.hasAnimatedTransform = true;
      if (!this.isSticky && shouldStick) {
        this.hasAnimatedTransform = false;
      }
      if (this.isSticky && !shouldStick) {
        this.hasAnimatedTransform = false;
      }
      this.isSticky = shouldStick;
    }

    get hasTableOfContent() {
      const hasTableOfContent = this.props.page.data.hasTableOfContent;
      if (typeof hasTableOfContent === 'string') {
        return hasTableOfContent === 'true';
      } else {
        return hasTableOfContent;
      }
    }

    @observable isSticky = false;
    @observable hasAnimatedTransform = false;

    render() {
      const {
        children,
        page,
        isLoggedIn,
        dashboardAccess,
        contentState, // eslint-disable-line
        decoratedText, // eslint-disable-line
        entityKey, // eslint-disable-line
        offsetKey, // eslint-disable-line
        //readOnly,
        appStore,
        isHeaderVisisble,
        classes,
        ...otherProps
      } = this.props;
      const { data } = page;
      const readOnly = !appStore.editMode;
      const hasWriteAccess = !readOnly;
      const hasAdminHeader = isLoggedIn && dashboardAccess;

      const editorOffset = hasAdminHeader ? 71 : 0;
      const navigationOffset = isHeaderVisisble ? 0 : 0;
      const topOffset = navigationOffset + editorOffset;
      const ownHeight = 50;
      const showTOC = hasWriteAccess || this.hasTableOfContent;
      return (
        <div
          className={cx(classes.root, { [classes.rootSpacing]: showTOC })}
          {...otherProps}
        >
          {children}
          {showTOC && (
            <Waypoint
              topOffset={topOffset - ownHeight}
              onPositionChange={({ currentPosition }) => {
                if (currentPosition === 'above') {
                  this.setSticky(true);
                } else {
                  this.setSticky(false);
                }
              }}
            >
              <div className={classes.waypointMarker} />
            </Waypoint>
          )}
          {showTOC && (
            <TocContainer
              hasAnimatedTransform={this.hasAnimatedTransform}
              isSticky={this.isSticky && this.hasTableOfContent}
              isHeaderVisisble={isHeaderVisisble}
            >
              <TOC
                page={page}
                {...config}
                {...otherProps}
                readOnly={readOnly}
                isSticky={this.isSticky}
              />
            </TocContainer>
          )}
        </div>
      );
    }
  }

  const ScrollTriggerWrapper = props => {
    const isHeaderVisisble = useScrollTrigger();
    return <TitleWithToc isHeaderVisisble={isHeaderVisisble} {...props} />;
  };

  return ScrollTriggerWrapper;
};

export default createTitleWithToc;
